import { Button, FormRow, FormSet, H1, Input, Section } from '@dnb/eufemia';
import { OrganizationDto, organizationInputSchema } from '@portals/shared/admin/OrganizationDto';
import { useEufemiaForm } from '@portals/shared-frontend/hooks';
import { type JSX, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';

import { saveOrganization } from '@/api/organizations';
import BackButton from '@/components/BackButton';
import Container from '@/components/Container';
import LoadingModal from '@/components/LoadingModal';
import LoadingPage from '@/components/LoadingPage';

export default function OrganizationForm(): JSX.Element {
  const navigate = useNavigate();
  const { organizationId } = useParams();

  const { data: organization, isValidating: organizationLoading } = useSWR<OrganizationDto>(
    organizationId && `/organizations/${organizationId}`,
  );

  const {
    controller: { setValues },
    controller,
    register,
    handleSubmit,
    submitting,
  } = useEufemiaForm(organizationInputSchema, {
    domain: null,
    ciamUatPartyId: null,
    ciamProdPartyId: null,
    tppId: null,
  });

  useEffect(() => {
    if (controller.values.domain === '') {
      controller.setValue('domain', null);
    }
    if (controller.values.ciamProdPartyId === '') {
      controller.setValue('ciamProdPartyId', null);
    }
    if (controller.values.ciamUatPartyId === '') {
      controller.setValue('ciamUatPartyId', null);
    }
    if (controller.values.tppId === '') {
      controller.setValue('tppId', null);
    }
  }, [controller]);

  useEffect(() => {
    if (organization) {
      setValues(organization);
    }
  }, [organization, setValues]);

  const onSubmit = handleSubmit(
    useCallback(
      async (data) => {
        await saveOrganization(data);
        navigate('..');
      },
      [navigate],
    ),
  );

  const title = organization ? 'Edit organization' : 'Add organization';

  if (organizationLoading) {
    return <LoadingPage />;
  }

  return (
    <>
      <BackButton to="/organizations">Organizations</BackButton>
      <Container centered size="small">
        {submitting && <LoadingModal />}

        <H1 bottom={false} top="large">
          {title}
        </H1>

        <FormSet on_submit={onSubmit} vertical>
          <Section spacing="x-large" style_type="white">
            <FormRow>
              <Input label="Name" stretch {...register.input('name')} />
            </FormRow>
            {!organizationId && (
              <FormRow top="medium">
                <Input label="Domain" stretch {...register.input('domain')} />
              </FormRow>
            )}
            <FormRow>
              <Input label="Ciam Uat Party Id" stretch top="medium" {...register.input('ciamUatPartyId')} />
            </FormRow>
            <FormRow>
              <Input label="Ciam Prod Party Id" stretch top="medium" {...register.input('ciamProdPartyId')} />
            </FormRow>
            <FormRow>
              <Input label="TPP Id" stretch top="medium" {...register.input('tppId')} />
            </FormRow>
          </Section>
          <Section spacing="medium" style_type="divider">
            <Button disabled={submitting} icon="save" icon_position="left" type="submit">
              Save
            </Button>
          </Section>
        </FormSet>
      </Container>
    </>
  );
}
